<template>
    <!-- Manual, per-pillar prompt page (PLT-1506, PLT-1613). Two steps:
         1) Run ask — render this pillar's ask-prompt with the edited variables, run it, and persist the
            bounded factual answer as a scoped WorkIQ snapshot.
         2) Distill & write — distill that answer into structured insights, stored on the same snapshot. -->
    <div class="flex flex-col gap-5">
        <div class="min-w-0">
            <div class="body-bold text-ink">{{ spec.label }}</div>
            <div class="body-small text-ink-muted">Render this pillar's ask-prompt with editable variables, run it, then distill &amp; write.</div>
        </div>

        <div v-if="!connected" class="body-small text-ink-muted">Connect Work IQ in the Prompt tab to run this pillar.</div>

        <!-- Variable inputs -->
        <div v-if="spec.variables.length" class="flex flex-col gap-3">
            <label v-for="variable in spec.variables" :key="variable.name" :for="`workiq-pillar-${spec.key}-${variable.name}`" class="flex flex-col gap-1">
                <span class="body-small-bold text-ink">{{ variable.label }}</span>
                <BaseTextarea
                    v-if="variable.multiline"
                    :id="`workiq-pillar-${spec.key}-${variable.name}`"
                    v-model:input="form[variable.name]"
                    disable-autosize
                    show-resize-handle
                    rows="3"
                    :data-testid="`workiq-pillar-var-${variable.name}`"
                    class="workiq-field" />
                <BaseInput
                    v-else
                    :id="`workiq-pillar-${spec.key}-${variable.name}`"
                    v-model="form[variable.name]"
                    type="text"
                    :data-testid="`workiq-pillar-var-${variable.name}`" />
            </label>
        </div>

        <!-- Editable ask-prompt template (iterate on the prompt itself; {{ variables }} are rendered in) -->
        <div class="flex flex-col gap-1">
            <label :for="`workiq-pillar-${spec.key}-ask-prompt`" class="flex flex-col gap-1">
                <span class="body-small-bold text-ink">Ask prompt</span>
                <BaseTextarea
                    :id="`workiq-pillar-${spec.key}-ask-prompt`"
                    v-model:input="askPrompt"
                    disable-autosize
                    show-resize-handle
                    rows="10"
                    data-testid="workiq-pillar-ask-prompt"
                    class="workiq-field font-mono" />
            </label>
            <BaseButton theme="tertiary" size="sm" class="self-start" @click="resetAskPrompt">Reset to default</BaseButton>
        </div>

        <!-- Step 1: run the ask -->
        <div class="flex items-center gap-3">
            <BaseButton theme="primary" size="sm" data-testid="workiq-pillar-run-ask" :disabled="asking || !connected" @click="runAsk">
                {{ asking ? "Running…" : "Run ask" }}
            </BaseButton>
            <span v-if="asking || askElapsedMs > 0" class="text-xs tabular-nums text-ink-muted">{{ askElapsedLabel }}</span>
        </div>

        <BaseNotification v-if="askError" theme="error" hide-dismiss>
            <template #content
                ><p class="body-small whitespace-pre-wrap break-words">{{ askError }}</p></template
            >
        </BaseNotification>

        <BaseCollapsible v-if="renderedPrompt" class="text-ink">
            <template #trigger><span class="body-small text-ink-muted">Prompt sent</span></template>
            <template #content>
                <pre class="mt-1 details font-mono whitespace-pre-wrap break-words bg-surface-sunken rounded-md p-2">{{ renderedPrompt }}</pre>
            </template>
        </BaseCollapsible>

        <!-- Snapshot scope confirmation: what the ask stored, and until when it stays current. -->
        <BaseNotification v-if="askSnapshot" data-testid="workiq-pillar-snapshot-scope" theme="success" hide-dismiss>
            <template #content
                ><div class="body-small flex flex-col gap-1">
                    <div class="font-semibold">Snapshot saved</div>
                    <div>Scope: {{ scopeSummary(askSnapshot) }}</div>
                    <div class="text-xs">Period: {{ askSnapshot.prepared_period }} · Expires: {{ formatExpiry(askSnapshot.expires_at) }}</div>
                </div></template
            >
        </BaseNotification>

        <div v-if="answer" class="flex flex-col gap-2">
            <div class="body-bold text-ink">Answer</div>
            <BaseCard padding="sm"><MarkdownText :text="answer" class="prose prose-sm max-w-none text-ink" /></BaseCard>
        </div>

        <!-- Step 2: distill + write the reviewed answer (only for pillars that produce structured insights) -->
        <template v-if="answer && spec.distillable">
            <HorizontalDivider as="hr" spacing="none" />
            <div class="flex items-center justify-between gap-4">
                <div class="min-w-0">
                    <div class="body-bold text-ink">Distill &amp; write</div>
                    <div class="body-small text-ink-muted">Distill the answer above into structured insights, stored on this {{ spec.label }} snapshot.</div>
                </div>
            </div>

            <!-- Editable distill-prompt template (the structured-output messages YAML) -->
            <div class="flex flex-col gap-1">
                <label :for="`workiq-pillar-${spec.key}-distill-prompt`" class="flex flex-col gap-1">
                    <span class="body-small-bold text-ink">Distill prompt</span>
                    <BaseTextarea
                        :id="`workiq-pillar-${spec.key}-distill-prompt`"
                        v-model:input="distillPrompt"
                        disable-autosize
                        show-resize-handle
                        rows="12"
                        data-testid="workiq-pillar-distill-prompt"
                        class="workiq-field font-mono" />
                </label>
                <BaseButton theme="tertiary" size="sm" class="self-start" @click="resetDistillPrompt">Reset to default</BaseButton>
            </div>

            <div class="flex items-center gap-3">
                <BaseButton theme="primary" size="sm" data-testid="workiq-pillar-distill" :disabled="distilling" @click="runDistill">
                    {{ distilling ? "Distilling…" : "Distill &amp; write" }}
                </BaseButton>
                <span v-if="distilling || distillElapsedMs > 0" class="text-xs tabular-nums text-ink-muted">{{ distillElapsedLabel }}</span>
            </div>

            <BaseNotification v-if="distillError" theme="error" hide-dismiss>
                <template #content
                    ><p class="body-small whitespace-pre-wrap break-words">{{ distillError }}</p></template
                >
            </BaseNotification>

            <div v-if="distillStatus" class="body-small text-ink-muted">
                Status: <span class="font-semibold">{{ distillStatus }}</span>
                <template v-if="distillReason"> ({{ distillReason }})</template>
                — {{ distilled.length }} insight{{ distilled.length === 1 ? "" : "s" }} written.
            </div>

            <BaseCard v-for="(insight, i) in distilled" :key="i" padding="sm" class="flex flex-col gap-2">
                <div class="flex items-center gap-2 flex-wrap">
                    <span class="body-bold text-ink">{{ insight.title }}</span>
                    <BaseChip v-if="insight.category" size="2xs" class="text-ink-muted">{{ insight.category }}</BaseChip>
                </div>
                <div class="body-small text-ink">{{ insight.body }}</div>
                <div v-if="insight.surface_from" class="text-xs text-ink-muted">Talk window: {{ insight.surface_from }} → {{ insight.surface_until }}</div>
                <div v-if="insight.coaching_question" class="text-xs italic text-ink-muted">“{{ insight.coaching_question }}”</div>
            </BaseCard>
        </template>
    </div>
</template>

<script lang="ts">
export interface PillarVariableSpec {
    name: string;
    label: string;
    multiline: boolean;
    default: string;
}

export interface PillarSpec {
    key: string;
    label: string;
    available: boolean;
    distillable: boolean;
    ask_prompt: string;
    distill_template: string;
    variables: PillarVariableSpec[];
}

// One distilled insight, as stored in WorkIqInsightSnapshot.distilled (WorkIqInsight.model_dump()).
export interface DistilledInsight {
    title: string;
    body: string;
    category: string | null;
    pillar: string | null;
    coaching_question: string | null;
    confidence: string | null;
    surface_from: string | null;
    surface_until: string | null;
    evidence: string[];
    unknowns: string[];
}

// A WorkIQ-owned snapshot (workiq.snapshots.serialize_snapshot).
export interface Snapshot {
    pillar: string;
    scope_key: string;
    prepared_period: string;
    scope_metadata: Record<string, unknown>;
    summary: string;
    distilled: DistilledInsight[] | null;
    expires_at: string | null;
    is_current: boolean;
}

interface DistillStatusResult {
    state?: string;
    ready?: boolean;
    status?: string;
    reason?: string;
    error?: string;
    snapshot?: Snapshot | null;
}
</script>

<script setup lang="ts">
import "~vue/components/workiq/playground.css";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { formatDate } from "~vue/utils/formatDate";
import { computed, onUnmounted, reactive, ref } from "vue";

const props = defineProps<{
    spec: PillarSpec;
    askUrl: string;
    distillUrl: string;
    statusUrl: string;
    distillTemplate: string;
    connected: boolean;
}>();

// Seed the variable form from each variable's per-user default.
const form = reactive<Record<string, string>>(Object.fromEntries(props.spec.variables.map((v) => [v.name, v.default])));

// Editable prompt templates — prefilled from the committed prompts so the staff user can iterate.
// Distill prefers the pillar-specific template, falling back to the shared one.
const defaultDistill = props.spec.distill_template || props.distillTemplate;
const askPrompt = ref(props.spec.ask_prompt);
const distillPrompt = ref(defaultDistill);
function resetAskPrompt() {
    askPrompt.value = props.spec.ask_prompt;
}
function resetDistillPrompt() {
    distillPrompt.value = defaultDistill;
}

// Render a snapshot's scope metadata (names/goals/roster) as a short readable line.
function scopeSummary(snapshot: Snapshot): string {
    const parts = Object.entries(snapshot.scope_metadata || {})
        .map(([key, value]) => {
            const rendered = Array.isArray(value) ? value.join(", ") : String(value ?? "");
            return rendered ? `${key}: ${rendered}` : "";
        })
        .filter(Boolean);
    return parts.length ? parts.join(" · ") : snapshot.scope_key;
}

function formatExpiry(iso: string | null): string {
    return iso ? formatDate(iso, "datetime") : "—";
}

// --- Step 1: ask ---
const asking = ref(false);
const answer = ref("");
const renderedPrompt = ref("");
const askError = ref("");
const askSnapshot = ref<Snapshot | null>(null);
const askElapsedMs = ref(0);
let askTimerStart = 0;
let askTimerHandle: ReturnType<typeof setInterval> | undefined;
const askElapsedLabel = computed(() => `${(askElapsedMs.value / 1000).toFixed(1)}s`);

async function runAsk() {
    if (asking.value || !props.connected) {
        return;
    }
    asking.value = true;
    answer.value = "";
    renderedPrompt.value = "";
    askError.value = "";
    askSnapshot.value = null;
    askElapsedMs.value = 0;
    askTimerStart = performance.now();
    askTimerHandle = setInterval(() => (askElapsedMs.value = performance.now() - askTimerStart), 100);
    try {
        const { data, response } = await useFetch(props.askUrl)
            .post({ pillar: props.spec.key, variables: { ...form }, prompt_template: askPrompt.value })
            .json();
        if (response.value?.ok) {
            answer.value = data.value?.answer || "(empty response)";
            renderedPrompt.value = data.value?.rendered_prompt || "";
            askSnapshot.value = data.value?.snapshot || null;
        } else {
            askError.value = data.value?.error || "Request failed.";
        }
    } finally {
        if (askTimerHandle) {
            clearInterval(askTimerHandle);
            askTimerHandle = undefined;
        }
        askElapsedMs.value = performance.now() - askTimerStart;
        asking.value = false;
    }
}

// --- Step 2: distill + write (async on a worker, then poll) ---
const distilling = ref(false);
const distillError = ref("");
const distillStatus = ref("");
const distillReason = ref("");
const distilled = ref<DistilledInsight[]>([]);
const distillElapsedMs = ref(0);
let distillTimerStart = 0;
let distillTimerHandle: ReturnType<typeof setInterval> | undefined;
let distillPollHandle: ReturnType<typeof setTimeout> | undefined;
const distillElapsedLabel = computed(() => `${(distillElapsedMs.value / 1000).toFixed(1)}s`);

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_MS = 6 * 60 * 1000;

function finishDistill() {
    if (distillTimerHandle) {
        clearInterval(distillTimerHandle);
        distillTimerHandle = undefined;
    }
    distillElapsedMs.value = performance.now() - distillTimerStart;
    distilling.value = false;
}

function applyDistillResult(d: DistillStatusResult) {
    if (d.state === "FAILURE") {
        distillError.value = d.error || "Distill task failed.";
        return;
    }
    distillStatus.value = d.status || "";
    distillReason.value = d.reason || "";
    distillError.value = d.error || "";
    if (d.status === "completed") {
        distilled.value = d.snapshot?.distilled || [];
    }
}

async function pollDistill(taskId: string) {
    const url = `${props.statusUrl}?task_id=${encodeURIComponent(taskId)}`;
    const tick = async () => {
        const { data, response } = await useFetch(url).get().json();
        if (!response.value?.ok) {
            distillError.value = data.value?.error || "Status check failed.";
            finishDistill();
            return;
        }
        if (!data.value?.ready) {
            if (performance.now() - distillTimerStart > MAX_POLL_MS) {
                distillError.value = "Timed out waiting for the distill. Is the Celery worker running?";
                finishDistill();
                return;
            }
            distillPollHandle = setTimeout(tick, POLL_INTERVAL_MS);
            return;
        }
        applyDistillResult(data.value);
        finishDistill();
    };
    await tick();
}

async function runDistill() {
    if (distilling.value || !answer.value) {
        return;
    }
    distilling.value = true;
    distillError.value = "";
    distillStatus.value = "";
    distillReason.value = "";
    distilled.value = [];
    distillElapsedMs.value = 0;
    distillTimerStart = performance.now();
    distillTimerHandle = setInterval(() => (distillElapsedMs.value = performance.now() - distillTimerStart), 100);

    // Send the same variables used for the ask so the worker rebuilds the identical scope and upserts
    // the snapshot the ask wrote (rather than a mismatched one).
    const { data, response } = await useFetch(props.distillUrl)
        .post({ pillar: props.spec.key, answer: answer.value, variables: { ...form }, distill_template: distillPrompt.value })
        .json();
    if (!response.value?.ok) {
        distillError.value = data.value?.error || "Failed to start distill.";
        finishDistill();
        return;
    }
    const taskId = data.value?.task_id;
    if (!taskId) {
        distillError.value = "No task id returned.";
        finishDistill();
        return;
    }
    await pollDistill(taskId);
}

onUnmounted(() => {
    if (askTimerHandle) {
        clearInterval(askTimerHandle);
    }
    if (distillTimerHandle) {
        clearInterval(distillTimerHandle);
    }
    if (distillPollHandle) {
        clearTimeout(distillPollHandle);
    }
});
</script>
